import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Ribbon, Video, Gavel, Gift, ShieldCheck, Radio, Store,
  Trophy, Sun, Moon, Check, Zap, Heart, CreditCard, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";

const HERO_IMG = "https://images.unsplash.com/photo-1595351298020-038700609878?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const HERO_TILE = "https://images.unsplash.com/photo-1622618991746-fe6004db3a47?crop=entropy&cs=srgb&fm=jpg&q=85&w=500";
const LIVE_IMG = "https://images.unsplash.com/photo-1664277497095-424e085175e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const SELLER_IMG = "https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?crop=entropy&cs=srgb&fm=jpg&q=85&w=700";

const AVATARS = ["Amina", "Yusuf", "Layla", "Omar", "Sana"].map(
  (s) => `https://api.dicebear.com/7.x/initials/svg?seed=${s}&backgroundColor=0C4A38`
);

const FEATURES = [
  { icon: Video, title: "Live shopping shows", body: "Watch sellers showcase products in real time and shop the moment you fall in love.", wide: true, live: true, tag: "LIVE" },
  { icon: Gavel, title: "Auctions & bidding", body: "Win items in fast, real-time auctions or grab them instantly at a set price." },
  { icon: Gift, title: "Live giveaways", body: "Join free giveaways mid-show for a chance to win — no purchase needed." },
  { icon: ShieldCheck, title: "Verified Muslim sellers", body: "Shop with confidence from a trusted, vetted community of sellers." },
  { icon: Radio, title: "Go live everywhere", body: "Broadcast once and simulcast straight to YouTube, Facebook & Twitch." },
  { icon: Store, title: "Sync your store", body: "Connect Shopify or WooCommerce — or import a CSV — to keep prices & stock in sync automatically.", wide: true, tag: "SELLERS", tagGold: true },
];

const STEPS = [
  { n: "1", title: "Create your free account", body: "Sign up in seconds — it's free, no invite needed." },
  { n: "2", title: "Join a live show", body: "Browse sellers going live and jump straight into the action." },
  { n: "3", title: "Bid, buy & checkout", body: "Grab deals and pay securely with Stripe — right from your screen." },
];

const SELLER_POINTS = [
  { icon: Store, title: "One-click store sync", body: "Import your Shopify, WooCommerce or CSV catalog in minutes." },
  { icon: CreditCard, title: "Instant Stripe payouts", body: "Get paid directly with Stripe Connect — no middleman." },
  { icon: Zap, title: "Zero upfront cost", body: "Open your storefront and go live free. Keep more of every sale." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { mode, toggle } = useTheme();
  const [founding, setFounding] = useState(null);
  const [refData, setRefData] = useState(null);

  useEffect(() => {
    document.documentElement.classList.add("lp-active");
    api.get("/public/founding").then(setFounding).catch(() => {});
    api.get("/referrals/public/winners").then(setRefData).catch(() => {});
    return () => document.documentElement.classList.remove("lp-active");
  }, []);

  const prizeText = (p) => {
    if (!p) return "";
    if (p.type === "cash" && p.value) return `$${Number(p.value).toFixed(0)} cash`;
    if (p.type === "product" && p.label) return p.label;
    return p.label || "";
  };
  const latest = refData?.latest;
  const currentPrize = prizeText(refData?.current_prize);
  const MEDALS = ["🥇", "🥈", "🥉", "🏅", "🏅"];

  const STATS = [
    { num: founding ? founding.limit.toLocaleString() : "1,500", label: "Founding Member spots" },
    { num: "100%", label: "Secure Stripe checkout" },
    { num: "Live", label: "Real-time auctions & giveaways" },
    { num: "0%", label: "Upfront cost to start selling" },
  ];

  return (
    <div className="lp fade-up" data-testid="landing-page">
      {/* Nav */}
      <nav className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <div style={{ cursor: "pointer" }} onClick={() => navigate("/landing")} data-testid="nav-logo"><Logo height={40} showWordmark /></div>
          <div className="lp-nav-links">
            <button className="lp-nav-link" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} data-testid="nav-features">Features</button>
            <button className="lp-nav-link" onClick={() => document.getElementById("sellers")?.scrollIntoView({ behavior: "smooth" })} data-testid="nav-sellers">For sellers</button>
            <button className="lp-nav-link" onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} data-testid="nav-how">How it works</button>
            <button className="lp-nav-link" onClick={() => navigate("/pitch")} data-testid="nav-pitch">Pitch deck</button>
          </div>
          <div className="lp-nav-actions">
            <button className="lp-icon-btn" onClick={toggle} data-testid="nav-theme-toggle" title="Toggle theme">
              {mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate("/login")} data-testid="nav-login">Log in</button>
            <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => navigate("/signup")} data-testid="nav-join">Join free</button>
          </div>
        </div>
      </nav>

      {/* Founding urgency bar */}
      {founding && founding.remaining > 0 && (
        <div className="lp-founding" data-testid="landing-founding-banner">
          <Ribbon size={16} />
          The first <b>{founding.limit.toLocaleString()}</b> members become Founding Members
          <span className="lp-founding-spots">only {founding.remaining.toLocaleString()} spots left</span>
        </div>
      )}

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-container lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow"><span className="ss-dot" /> Live marketplace</span>
            <h1 className="lp-h1">Shop live from <span className="lp-gold">Muslim sellers</span>, worldwide</h1>
            <p className="lp-sub">Bid on auctions, grab buy-it-now deals, and join live giveaways — all in real time, from a community you can trust.</p>
            <div className="lp-cta-row">
              <button className="ss-btn ss-btn-gold lp-btn-lg" onClick={() => navigate("/signup")} data-testid="landing-join">
                Join SouqStream — it's free <ArrowRight size={18} />
              </button>
              <button className="ss-btn ss-btn-ghost lp-btn-lg" onClick={() => navigate("/login")} data-testid="landing-login">
                I already have an account
              </button>
            </div>
            <div className="lp-trust-row">
              <div className="lp-avatars">{AVATARS.map((a, i) => <img key={i} src={a} alt="" />)}</div>
              <div className="lp-trust-text">Join a growing community of <b>buyers & sellers</b> shopping live every day.</div>
            </div>
          </div>

          <div className="lp-visual">
            <img className="lp-visual-main" src={HERO_IMG} alt="Artisan seller crafting handmade goods" />
            <img className="lp-visual-tile" src={HERO_TILE} alt="Curated luxury products" />
            <div className="lp-float lp-float-live" data-testid="hero-float-live">
              <div className="lp-float-icon" style={{ background: "var(--red)", color: "#fff" }}><span className="ss-dot" style={{ background: "#fff" }} /></div>
              <div><small>Live now</small><strong>142 bidding</strong></div>
            </div>
            <div className="lp-float lp-float-verified" data-testid="hero-float-verified">
              <div className="lp-float-icon" style={{ background: "var(--gold-soft)", color: "var(--brand)" }}><ShieldCheck size={20} /></div>
              <div><small>Verified</small><strong>Trusted seller</strong></div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / press band */}
      <section className="lp-trust-band" data-testid="trust-band">
        <div className="lp-container">
          <div className="lp-trust-tag">Where <span>Live Shopping</span>, <span>Community</span> &amp; <span>Giving</span> come together</div>
          <div className="lp-stats">
            {STATS.map((s) => (
              <div className="lp-stat" key={s.label}>
                <div className="lp-stat-num">{s.num}</div>
                <div className="lp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="lp-section" id="features">
        <div className="lp-container">
          <div className="lp-section-head">
            <div className="lp-kicker">Everything happens live</div>
            <h2 className="lp-h2">A marketplace built for real-time discovery</h2>
            <p className="lp-lead">From fast-paced auctions to one-tap storefront sync, SouqStream gives buyers the thrill and sellers the tools.</p>
          </div>
          <div className="lp-bento">
            {FEATURES.map((f) => (
              <div className={`lp-card${f.wide ? " wide" : ""}${f.live ? " feature-live" : ""}`} key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {f.live && <img className="lp-card-img" src={LIVE_IMG} alt="" />}
                {f.tag && <span className={`lp-card-tag${f.tagGold ? " gold" : ""}`}>{!f.tagGold && <span className="ss-dot" style={{ background: "#fff" }} />}{f.tag}</span>}
                <div className="lp-card-ic"><f.icon size={24} /></div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seller value */}
      <section className="lp-section" id="sellers" style={{ paddingTop: 0 }} data-testid="seller-value-section">
        <div className="lp-container">
          <div className="lp-seller">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="lp-kicker">For sellers</div>
              <h2>Turn your store into a live show — and get paid fast</h2>
              <ul className="lp-seller-list">
                {SELLER_POINTS.map((p) => (
                  <li key={p.title}>
                    <span className="lp-seller-check"><Check size={16} /></span>
                    <div><strong>{p.title}</strong><span>{p.body}</span></div>
                  </li>
                ))}
              </ul>
              <div className="lp-cta-row">
                <button className="ss-btn ss-btn-gold lp-btn-lg" onClick={() => navigate("/signup")} data-testid="seller-apply-btn">
                  Start selling free <ArrowRight size={18} />
                </button>
              </div>
            </div>
            <div className="lp-seller-visual">
              <img src={SELLER_IMG} alt="Seller packing customer orders" />
              <div className="lp-payout" data-testid="seller-payout-card">
                <small>Paid out instantly</small>
                <strong>$1,248.00</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lp-section" id="how" style={{ paddingTop: 0 }}>
        <div className="lp-container">
          <div className="lp-section-head center">
            <div className="lp-kicker">Get started in minutes</div>
            <h2 className="lp-h2">Three steps to your first live deal</h2>
          </div>
          <div className="lp-steps">
            {STEPS.map((s) => (
              <div className="lp-step" key={s.n} data-testid={`step-${s.n}`}>
                <div className="lp-step-num">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Refer & win */}
      {(latest?.winners?.length > 0 || currentPrize) && (
        <section className="lp-section" data-testid="landing-referral-section" style={{ paddingTop: 0 }}>
          <div className="lp-container">
            <div className="lp-section-head center">
              <div className="lp-kicker"><Trophy size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />Refer &amp; win</div>
              <h2 className="lp-h2">
                {currentPrize ? <>Refer friends &amp; win <span className="lp-gold">{currentPrize}</span> this month</> : "Refer friends & win monthly prizes"}
              </h2>
              <p className="lp-lead" style={{ margin: "14px auto 0" }}>The top 5 members who refer active friends win every single month.</p>
            </div>
            {latest?.winners?.length > 0 && (
              <div className="lp-leader" data-testid="landing-winners">
                <div className="lp-leader-head">🏆 {latest.month_label} winners{prizeText(latest.prize) ? ` · ${prizeText(latest.prize)}` : ""}</div>
                {latest.winners.map((w) => (
                  <div className="lp-leader-row" key={w.rank}>
                    <span className="lp-leader-rank">{MEDALS[w.rank - 1] || `#${w.rank}`}</span>
                    <img src={w.avatar} alt="" />
                    <span className="lp-leader-name">{w.name}</span>
                    <span className="lp-leader-count">{w.count} referrals</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button className="ss-btn ss-btn-gold lp-btn-lg" onClick={() => navigate("/signup")} data-testid="landing-refer-join">Join &amp; start referring <ArrowRight size={16} /></button>
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-container">
          <div className="lp-cta-band" data-testid="bottom-cta-banner">
            <h2>Ready to join the souq?</h2>
            <p>Create your free account and start shopping live today.</p>
            <div className="lp-cta-row">
              <button className="ss-btn ss-btn-primary lp-btn-lg" onClick={() => navigate("/signup")} data-testid="landing-join-bottom">
                Join now <ArrowRight size={18} />
              </button>
              <button className="ss-btn ss-btn-ghost lp-btn-lg" style={{ background: "rgba(255,255,255,.35)", borderColor: "var(--brand-2)", color: "var(--brand-2)" }} onClick={() => navigate("/pitch")} data-testid="cta-pitch">
                <Sparkles size={16} /> View pitch deck
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer" data-testid="main-footer">
        <div className="lp-container">
          <div className="lp-footer-top">
            <Logo height={38} showWordmark />
            <div className="lp-footer-links">
              <button onClick={() => navigate("/about")} data-testid="footer-about">About</button>
              <button onClick={() => navigate("/contact")} data-testid="footer-contact">Contact</button>
              <button onClick={() => navigate("/privacy")} data-testid="footer-privacy">Privacy</button>
              <button onClick={() => navigate("/terms")} data-testid="footer-terms">Terms</button>
              <button onClick={() => navigate("/seller-policy")} data-testid="footer-seller-policy">Seller Policy</button>
              <button onClick={() => navigate("/pitch")} data-testid="footer-pitch">Pitch Deck</button>
              <button onClick={() => navigate("/delete-account")} data-testid="footer-delete">Delete account</button>
            </div>
          </div>
          <div className="lp-footer-bottom">
            © {new Date().getFullYear()} Contract 2 Cash LLC — Where Live Shopping, Community &amp; Giving Come Together.{" "}
            Developed by <a href="https://www.inubaanlabs.com" target="_blank" rel="noopener noreferrer" data-testid="footer-inubaan">Inubaan Labs</a>.
          </div>
        </div>
      </footer>
    </div>
  );
}
